"use client";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "@/lib/i18n";
import {
    ShieldCheckIcon,
    AlertTriangleIcon,
    BrainCircuitIcon,
    FileSearchIcon,
    WorkflowIcon,
    BarChart3Icon,
} from "lucide-react";

const pillars = (t: any) => [
    {
        title: t("grid_features.p1_title"),
        description: t("grid_features.p1_desc"),
        features: [
            t("grid_features.p1_f1"),
            t("grid_features.p1_f2"),
            t("grid_features.p1_f3"),
        ],
        icon: ShieldCheckIcon,
        span: "col-span-12 md:col-span-6 lg:col-span-5",
    },
    {
        title: t("grid_features.p2_title"),
        description: t("grid_features.p2_desc"),
        features: [
            t("grid_features.p2_f1"),
            t("grid_features.p2_f2"),
            t("grid_features.p2_f3"),
        ],
        icon: AlertTriangleIcon,
        span: "col-span-12 md:col-span-6 lg:col-span-7",
    },
    {
        title: t("grid_features.p3_title"),
        description: t("grid_features.p3_desc"),
        features: [
            t("grid_features.p3_f1"),
            t("grid_features.p3_f2"),
            t("grid_features.p3_f3"),
        ],
        icon: BrainCircuitIcon,
        span: "col-span-12 md:col-span-6 lg:col-span-7",
    },
    {
        title: t("grid_features.p4_title"),
        description: t("grid_features.p4_desc"),
        features: [
            t("grid_features.p4_f1"),
            t("grid_features.p4_f2"),
            t("grid_features.p4_f3"),
        ],
        icon: WorkflowIcon,
        span: "col-span-12 md:col-span-6 lg:col-span-5",
    },
];

export function GridFeatures() {
    const { t } = useTranslation("common");
    return (
        <section
            className="bg-background px-4 py-12 text-foreground sm:py-24 md:py-32"
            id="features"
        >
            <div className="mx-auto flex max-w-[1200px] flex-col items-center gap-6 sm:gap-12">
                <h2 className="text-balance text-center text-3xl font-semibold sm:text-5xl">
                    {t("grid_features.main_title")}
                </h2>
                <p className="text-md max-w-[720px] text-balance text-center font-medium text-muted-foreground sm:text-xl">
                    {t("grid_features.subtitle")}
                </p>
                <div className="grid gap-6 sm:gap-4">
                    <div className="grid grid-cols-12 gap-4">
                        {pillars(t).slice(0, 2).map((pillar: any) => (
                            <Card
                                key={pillar.title}
                                className={`group relative flex flex-col gap-6 overflow-hidden rounded-xl p-6 text-card-foreground shadow-xl transition-all ${pillar.span}`}
                            >
                                <CardContent className="p-6">
                                    <div className="flex flex-col gap-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-aicompliance-foreground/10">
                                                <pillar.icon className="h-5 w-5 text-aicompliance-foreground" />
                                            </div>
                                            <h3 className="text-2xl font-semibold leading-none tracking-tight">
                                                {pillar.title}
                                            </h3>
                                        </div>
                                        <p className="text-md text-balance text-muted-foreground">
                                            {pillar.description}
                                        </p>
                                        <ul className="mt-2 flex flex-col gap-2">
                                            {pillar.features.map((feature: string) => (
                                                <li
                                                    key={feature}
                                                    className="flex items-start gap-2 text-sm text-muted-foreground"
                                                >
                                                    <FileSearchIcon className="mt-0.5 h-4 w-4 shrink-0 text-aicompliance-foreground" />
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="flex grow items-end justify-center mt-6 -mb-6 opacity-20 transition-all duration-300 group-hover:opacity-30">
                                        <div className="absolute w-full bottom-0">
                                            <div className="absolute left-1/2 h-[128px] w-[60%] -translate-x-1/2 rounded-[50%] bg-[radial-gradient(ellipse_at_center,_hsla(var(--aicompliance-foreground)/.5)_10%,_hsla(var(--aicompliance-foreground)/0)_60%)] opacity-20 dark:opacity-100"></div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                    <div className="grid grid-cols-12 gap-4">
                        {pillars(t).slice(2, 4).map((pillar: any) => (
                            <Card
                                key={pillar.title}
                                className={`group relative flex flex-col gap-6 overflow-hidden rounded-xl p-6 text-card-foreground shadow-xl transition-all ${pillar.span}`}
                            >
                                <CardContent className="p-6">
                                    <div className="flex flex-col gap-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-aicompliance-foreground/10">
                                                <pillar.icon className="h-5 w-5 text-aicompliance-foreground" />
                                            </div>
                                            <h3 className="text-2xl font-semibold leading-none tracking-tight">
                                                {pillar.title}
                                            </h3>
                                        </div>
                                        <p className="text-md text-balance text-muted-foreground">
                                            {pillar.description}
                                        </p>
                                        <ul className="mt-2 flex flex-col gap-2">
                                            {pillar.features.map((feature: string) => (
                                                <li
                                                    key={feature}
                                                    className="flex items-start gap-2 text-sm text-muted-foreground"
                                                >
                                                    <BarChart3Icon className="mt-0.5 h-4 w-4 shrink-0 text-aicompliance-foreground" />
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="flex grow items-end justify-center mt-6 -mb-6 opacity-20 transition-all duration-300 group-hover:opacity-30">
                                        <div className="absolute w-full bottom-0">
                                            <div className="absolute left-1/2 h-[128px] w-[60%] -translate-x-1/2 rounded-[50%] bg-[radial-gradient(ellipse_at_center,_hsla(var(--aicompliance-foreground)/.5)_10%,_hsla(var(--aicompliance-foreground)/0)_60%)] opacity-20 dark:opacity-100"></div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
